// src/ecrans/accueil/EcranAccueil.jsx — L'ACCUEIL NEST (maquette v819,
// « Admin › Accueil »), en LECTURE SEULE.
//
// Ordinateur : en-tête (date, salutation, tuiles, ligne du mois) ; colonne
// principale « À décider » puis « Voyageurs qui attendent » ; colonne de côté
// l'opérateur, « Mes tâches », « L'argent du mois » ; « Remplissage » sur
// toute la largeur ; « Voir l'ancien accueil » en lien discret.
// Téléphone : « À décider » ouvert en lignes compactes, les autres blocs en
// cartes refermées, une seule ouverte à la fois (« Replier »).
//
// Lu SEULEMENT par les portes du serveur :
//   · ecran_journee_lire { jours: 1 }  → ménages, arrivées, départs,
//     salutation, couverture (la règle des chiffres, ../couverture.js) ;
//   · ecran_incidents_lire, ecran_reprises_lire, ecran_controles_lire,
//     services_orchestrateur_hourly_list, signalements_voyageur_list,
//     veille_manques, ecran_fermetures_lire       → « À décider » ;
//   · operateur_propositions_list      → « à traiter » ;
//   · ecran_conversations_lire en_attente → « Voyageurs qui attendent » ;
//   · operateur_fait_list              → « L'opérateur a fait N choses seul » ;
//   · ecran_taches_lire { vue: semaine } → « Mes tâches » ;
//   · ecran_logements_lire, ecran_calendrier_sejours_lire (30 jours)
//                                      → « Remplissage » ;
//   · /api/prospects?action=count      → « N annonces à démarcher ».
//
// HORS LOT (argent, lot 4) : la ligne du mois et « L'argent du mois » restent
// « — » ; aucune lecture ni aucun calcul d'argent ici. ÉCRITURES hors lot :
// « Traiter » mène à l'écran de la ligne (les fenêtres par-dessus l'Accueil
// viendront au lot 2) ; cocher ou ajouter une tâche (lot 3).
//
// LE MICRO d'une ligne ouvre l'opérateur. L'opérateur ne sait pas encore
// recevoir une question pré-remplie (« À propos de : … ») : il s'ouvre
// simplement ; rien ne part tout seul.

import { useEffect, useMemo, useState } from "react";
import { prospectsApi } from "../../lib/prospects-api";
import { useLecture } from "../portes";
import { StyleNest, EnTete, Tuile, EtatLecture, C, aujourdhuiParis, jourEnLettres, decalerIso, MOIS, mot } from "../nest";
import { chiffreSuivi, couvertureDe, etatSuivi, idsNonSuivis, jourSuivi } from "../couverture";
import useEstEtroit from "../useEstEtroit";
import { jourDe, lignesMenages, lignesMouvements, ceQuiVousAttend, aTraiter } from "./accueil-calculs";
import { CSS_ACCUEIL } from "./accueil-style";
import BlocADecider from "./BlocADecider";
import { BlocVoyageurs, LigneOperateur, BlocTaches, BlocArgent } from "./BlocsCote";
import BlocRemplissage from "./BlocRemplissage";

const SANS_OK = { accepteSansOk: true };

const LIGNE_MOIS_CSS = `
.nacc-mois{display:flex;gap:10px;align-items:baseline;font-size:12px;margin-top:10px;flex-wrap:wrap}
.nacc-mois .m{color:${C.grisD}}
.nacc-mois b{font-weight:600}
@media (max-width:640px){
 .nacc-mois{display:grid;grid-template-columns:1fr 1fr;gap:4px 14px;font-size:12.5px;margin-top:12px;padding-top:12px;border-top:1px solid ${C.bord}}
 .nacc-mois .m{grid-column:1/-1;text-transform:uppercase;letter-spacing:.06em;font-size:10.5px}
 .nacc-mois .p{display:none}
}
`;

/** Les prospects passent par leur propre porte (/api/prospects). */
function useProspection() {
  const [l, setL] = useState({ etat: "lecture" });
  useEffect(() => {
    let vivant = true;
    prospectsApi
      .fetchProspectsCount()
      .then((j) => vivant && setL({ etat: "ok", data: j || {} }))
      .catch((e) => vivant && setL({ etat: "echec", motif: (e && e.message) || "Lecture impossible." }));
    return () => {
      vivant = false;
    };
  }, []);
  return l;
}

function LigneDetail({ l, onClick }) {
  const fond = l.ton === "vert" ? C.vert : l.ton === "aub" ? C.aub : "#98A2AD";
  return (
    <button type="button" className="nest-ligne" onClick={onClick}>
      <span
        aria-hidden="true"
        style={{ width: 28, height: 28, borderRadius: 7, background: fond, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 600, flexShrink: 0 }}
      >
        {l.ic}
      </span>
      <span className="co">
        <span className="t">{l.titre}</span>
        <span className="s">{l.sous}</span>
      </span>
      <span className="ch" aria-hidden="true">›</span>
    </button>
  );
}

const ECRANS_DIRECTS = ["menages", "incidents", "menage-controle", "calendrier", "operateur"];
/** L'écran où se traite une ligne de « À décider » (comme avant ce lot). */
export const ecranDe = (l) => (l.cible && ECRANS_DIRECTS.includes(l.cible.ecran) ? l.cible.ecran : "orchestrateur-horaires");

export default function EcranAccueil({ onAller, onOuvrirFil, onAncien }) {
  const today = useMemo(() => aujourdhuiParis(), []);
  const dans30 = useMemo(() => decalerIso(today, 30), [today]);
  const etroit = useEstEtroit();
  const [carte, setCarte] = useState(null);
  const [detail, setDetail] = useState(null);

  const [journee, relireJournee] = useLecture("ecran_journee_lire", { jours: 1 });
  const [logements] = useLecture("ecran_logements_lire", {});
  const [sejours] = useLecture("ecran_calendrier_sejours_lire", { du: today, au: dans30 });
  const [messages, relireMessages] = useLecture("ecran_conversations_lire", { filtre: "en_attente" });
  const [taches, relireTaches] = useLecture("ecran_taches_lire", { vue: "semaine" });
  const [propositions] = useLecture("operateur_propositions_list", {}, "", SANS_OK);
  const [fait] = useLecture("operateur_fait_list", {});
  const [veilles] = useLecture("veille_manques", {}, "", SANS_OK);
  const [horaires] = useLecture("services_orchestrateur_hourly_list", {}, "", SANS_OK);
  const [signalements] = useLecture("signalements_voyageur_list", {}, "", SANS_OK);
  const [incidents] = useLecture("ecran_incidents_lire", {});
  const [reprises] = useLecture("ecran_reprises_lire", {});
  const [controles] = useLecture("ecran_controles_lire", {});
  const [fermetures] = useLecture("ecran_fermetures_lire", {});
  const prospection = useProspection();

  const nomLogement = useMemo(() => {
    const m = new Map();
    if (logements.etat === "ok")
      for (const l of logements.data.logements || []) {
        m.set(l.id, l.nom);
        if (l.parent) m.set(l.parent.id, l.parent.nom);
      }
    return (id) => m.get(id) || id || "";
  }, [logements]);

  // LA RÈGLE DES CHIFFRES : un seul calcul, ../couverture.js.
  const cv = couvertureDe(journee);
  const suivi = etatSuivi(cv);
  // Le CHIFFRE d'une tuile ne compte que les logements SUIVIS (règle de la
  // couverture) ; le DÉTAIL qu'elle déplie liste toutes les lignes réelles,
  // celles des logements non suivis marquées « non suivi ». La règle « le
  // nombre d'une tuile est la longueur de sa liste » cède ici devant celle-là.
  const jourComplet = journee.etat === "ok" ? jourDe(journee.data) : null;
  const jour = jourSuivi(jourComplet, cv);
  const nonSuivis = idsNonSuivis(cv);
  const marquer = (lignes, items) =>
    lignes.map((l, k) =>
      items[k] && items[k].logement && nonSuivis.has(items[k].logement.id) ? { ...l, sous: `${l.sous} · non suivi` } : l,
    );
  const de = (cle) => (jourComplet && jourComplet[cle]) || [];
  const chiffreJour = (cle) => chiffreSuivi(jour ? (jour[cle] || []).length : NaN, cv);
  const tuiles = {
    menages: { c: chiffreJour("menages"), s: "ménage", p: "ménages", fin: " aujourd'hui", lignes: marquer(lignesMenages(jourComplet), de("menages")), vide: ["Aucun ménage aujourd'hui.", "Aucun ménage lu aujourd'hui : un logement sans source peut en cacher un."] },
    arrivees: { c: chiffreJour("arrivees"), s: "arrivée", p: "arrivées", fin: "", lignes: marquer(lignesMouvements(jourComplet, "arrivee"), de("arrivees")), vide: ["Aucune arrivée aujourd'hui.", "Aucune arrivée lue aujourd'hui : un logement sans source peut en cacher une."] },
    departs: { c: chiffreJour("departs"), s: "départ", p: "départs", fin: "", lignes: marquer(lignesMouvements(jourComplet, "depart"), de("departs")), vide: ["Aucun départ aujourd'hui.", "Aucun départ lu aujourd'hui : un logement sans source peut en cacher un."] },
  };

  const attend = ceQuiVousAttend({ journee, horaires, signalements, incidents, reprises, controles, fermetures, veilles, nomLogement });
  // Les lignes de « À décider » qui viennent d'un voyageur (demandes
  // d'horaire, signalements), quand ces deux lectures ont abouti.
  const demandesVoyageurs =
    horaires.etat === "ok" && signalements.etat === "ok"
      ? attend.lignes.filter((l) => l.nature === "demande_horaire" || l.nature === "signalement_voyageur").length
      : null;
  const props =
    propositions.etat === "ok" && propositions.data.actif !== false ? propositions.data.propositions || [] : [];
  const fils = messages.etat === "ok" ? messages.data.fils || [] : [];
  const traiter = aTraiter({
    nbPropositions: propositions.etat === "ok" ? props.length : NaN,
    attend,
    nbMessages: messages.etat === "ok" ? fils.length : NaN,
  });

  const mois = MOIS[(Number(today.slice(5, 7)) + 10) % 12];
  const moisLib = mois.charAt(0).toUpperCase() + mois.slice(1);
  const salut = journee.etat === "lecture" ? "…" : (journee.etat === "ok" && journee.data.salutation) || "Bonjour";

  const aller = (ecran) => onAller && onAller(ecran);
  // Le micro : l'opérateur s'ouvre (il ne reçoit pas encore de question pré-remplie).
  const parler = () => aller("operateur");
  const plie = (cle) =>
    etroit
      ? { ouverte: carte === cle, onOuvrir: () => setCarte(cle), onReplier: () => setCarte(null) }
      : null;

  return (
    <div className="nest" data-testid="ecran-accueil-nest">
      <StyleNest />
      <style>{CSS_ACCUEIL + LIGNE_MOIS_CSS}</style>
      <EnTete sous={jourEnLettres(today)} titre={salut}>
        <Tuile
          n={traiter.total === null ? (attend.enLecture ? "…" : "—") : traiter.total}
          libelle="à traiter ›"
          detail={traiter.detail}
          testId="accueil-a-traiter"
          onClick={() => {
            const e = typeof document !== "undefined" && document.querySelector('[data-testid="accueil-a-decider"]');
            if (e && e.scrollIntoView) e.scrollIntoView({ behavior: "smooth", block: "start" });
          }}
        />
        {["menages", "arrivees", "departs"].map((k) => {
          const t = tuiles[k];
          return (
            <Tuile
              key={k}
              n={t.c.valeur}
              detail={t.c.sur}
              libelle={`${mot(t.c.valeur, t.s, t.p)}${t.fin} ${detail === k ? "▲" : "▼"}`}
              ouverte={detail === k}
              testId={`accueil-tuile-${k}`}
              onClick={() => setDetail((d) => (d === k ? null : k))}
            />
          );
        })}
      </EnTete>
      <div style={{ background: "#fff", padding: "0 24px 14px", borderBottom: `1px solid ${C.bord}` }}>
        <EtatLecture lecture={journee} quoi="La journée" onRelire={relireJournee} />
        {journee.etat === "ok" && !suivi ? (
          <p className="nest-manque" data-testid="accueil-couverture-inconnue">— Couverture des sources inconnue : les chiffres du jour ne sont pas connus.</p>
        ) : null}
        {journee.etat === "ok" && suivi && suivi.aucun ? (
          <p className="nest-manque" data-testid="accueil-aucun-suivi">— Aucun logement suivi par une source de réservations : les chiffres du jour ne sont pas connus.</p>
        ) : null}
        {detail && journee.etat === "ok" ? (
          <div data-testid={`accueil-detail-${detail}`} style={{ marginTop: 4 }}>
            {tuiles[detail].lignes.length ? (
              tuiles[detail].lignes.map((l) => (
                <LigneDetail key={l.cle} l={l} onClick={() => aller(detail === "menages" ? "menages" : "calendrier")} />
              ))
            ) : (
              <p className="nest-etat">{tuiles[detail].vide[tuiles[detail].c.tousSuivis ? 0 : 1]}</p>
            )}
          </div>
        ) : null}
        <div data-testid="accueil-ligne-mois" className="nacc-mois">
          <span className="m">{moisLib}</span>
          <span className="v" title="Ligne du mois : chemin d'argent, pas encore lue ici.">
            <b>—</b> de résultat net (provisoire)
          </span>
          <span className="p" aria-hidden="true">·</span>
          <span className="v" title="Factures émises : chemin d'argent, pas encore lues ici.">
            <b>—</b> factures émises
          </span>
        </div>
      </div>

      <div className="nacc">
        <div className="nacc-col">
          <BlocADecider
            attend={attend}
            erreurs={[
              { lecture: journee, quoi: "Ménages du jour" },
              { lecture: horaires, quoi: "Demandes d'horaire" },
              { lecture: signalements, quoi: "Signalements" },
              { lecture: incidents, quoi: "Incidents" },
              { lecture: reprises, quoi: "Reprises" },
              { lecture: controles, quoi: "Contrôles" },
              { lecture: fermetures, quoi: "Fermetures de dates" },
              { lecture: veilles, quoi: "Veille de l'opérateur" },
            ]}
            onTraiter={(l) => aller(ecranDe(l))}
            onMicro={parler}
          />
          <BlocVoyageurs demandesADecider={demandesVoyageurs} messages={messages} onRelire={relireMessages} onOuvrirFil={onOuvrirFil} onMicro={parler} plie={plie("voyageurs")} />
        </div>
        <div className="nacc-col">
          <LigneOperateur fait={fait} today={today} onOuvrir={() => aller("operateur")} />
          <BlocTaches taches={taches} onRelire={relireTaches} onAller={aller} plie={plie("taches")} />
          <BlocArgent moisLib={moisLib} plie={plie("argent")} />
        </div>
        <div className="nacc-full">
          <BlocRemplissage
            logements={logements}
            sejours={sejours}
            couverture={cv}
            du={today}
            au={dans30}
            prospection={prospection}
            onAller={aller}
            plie={plie("remplissage")}
          />
        </div>
        {onAncien ? (
          <button type="button" className="nacc-ancien" onClick={onAncien} data-testid="accueil-ancien">
            Voir l'ancien accueil
          </button>
        ) : null}
      </div>
    </div>
  );
}
